import uid from "common/utils/uid";
import type { DocMigration } from "../docMigration";

// eslint-disable-next-line @typescript-eslint/naming-convention
const migration_0_0_363: DocMigration = {
  maxSchemaVersion: "0.0.363",
  description: "Convert WallAssemblyOverride to WallAssemblyOperator[]",
  apply(json: any) {
    if (!json.edgeBehaviors) {
      return json;
    }

    const foundRoomEdgeBehaviors: { [roomEdgeHash: string]: any[] } = {};

    for (const edgeBehaviorId in json.edgeBehaviors) {
      const edgeBehavior = json.edgeBehaviors[edgeBehaviorId];
      const { edgeBehaviorType, parentId, edgeId } = edgeBehavior;

      if (edgeBehaviorType !== "WALL_ASSEMBLY") {
        continue;
      }

      const room = json.rooms[parentId];
      if (!room) {
        delete json.edgeBehaviors[edgeBehaviorId];
        continue;
      }

      const roomEdgeHash = `${room.id}-${edgeId}`;
      if (!foundRoomEdgeBehaviors[roomEdgeHash]) {
        foundRoomEdgeBehaviors[roomEdgeHash] = [];
      }

      foundRoomEdgeBehaviors[roomEdgeHash].push(edgeBehavior);
      delete json.edgeBehaviors[edgeBehaviorId];
    }

    for (const roomEdgeHash in foundRoomEdgeBehaviors) {
      const edgeBehaviors = foundRoomEdgeBehaviors[roomEdgeHash];

      /**
       * Historically we have only been applying the first sorted item,
       * so this will match the current plans
       */
      edgeBehaviors.sort((a, b) => (a.sortIndex > b.sortIndex ? 1 : -1));
      const edgeBehavior = edgeBehaviors[0];

      const { parentId } = edgeBehavior;

      const room = json.rooms[parentId];

      const wallAssemblyOverride = edgeBehavior.wallAssemblyOverride as DeprecatedWallAssemblyOverride;
      let sortIndex = (edgeBehavior.sortIndex as number) ?? 0;

      // delete these here as we will be spreading the parent properties and
      // we don't want to include this property, since it it being migrated
      delete edgeBehavior.wallAssemblyOverride;
      delete edgeBehavior.name;
      delete edgeBehavior.id;
      delete edgeBehavior.sortIndex;
      delete edgeBehavior.disabled;

      const { leftOperators, rightOperators } = makeWallAssemblyOperatorsFromWallAssemblyOverride(
        wallAssemblyOverride,
        room,
      );

      for (let i = 0; i < leftOperators.length; i++) {
        const { wallAssemblyOperator, disabled, name } = leftOperators[i];
        const id = uid();
        const newEdgeBehavior = {
          ...edgeBehavior,
          id,
          name,
          sortIndex: sortIndex++,
          wallAssemblyOperator,
          disabled,
        };
        json.edgeBehaviors[id] = newEdgeBehavior;
      }

      for (let i = 0; i < rightOperators.length; i++) {
        const { wallAssemblyOperator, disabled, name } = rightOperators[i];
        const id = uid();
        const newEdgeBehavior = {
          ...edgeBehavior,
          id,
          name,
          sortIndex: sortIndex++,
          wallAssemblyOperator,
          outside: true,
          disabled,
        };
        json.edgeBehaviors[id] = newEdgeBehavior;
      }
    }

    return json;
  },
};

export default migration_0_0_363;

/**
 * Just some type safety here
 */
interface OffsetMatchExpr {
  readonly type: "MATCH_EXPR";
  readonly defaultValue?: number;
  readonly cases: {
    readonly condition: any; // must be converted to a disabled field on the EdgeBehavior
    readonly value: number;
  }[];
}

/**
 * Just enough Room information to perform this migration
 */
interface StaticMigrationRoom {
  readonly id: string;
  readonly type: string;
  readonly attributes: { readonly roomEnclosure: string };
}

/**
 * Just enough WallAssemblyLayer information to perform this migration
 */
interface StaticMigrationWallAssemblyLayer {
  /**
   * WallAssemblyLayers with a type property are ADD only
   */
  type?: string;
  disabled?: boolean;
}

/**
 * The input type for the migration.
 *
 * This interface represents the current user defined WallAssemblyOverride, stored on the Doc.
 * It needs to be refactored and migrated
 */
interface DeprecatedWallAssemblyOverride {
  readonly offset?: number | OffsetMatchExpr;
  readonly structuralLayerOverrides?: (StaticMigrationWallAssemblyLayer | null)[];
  readonly leftLayersOverrides?: (StaticMigrationWallAssemblyLayer | null)[];
  readonly rightLayersOverrides?: (StaticMigrationWallAssemblyLayer | null)[];
  readonly extraLeftFinishLayers?: (StaticMigrationWallAssemblyLayer | null)[];
  readonly extraRightFinishLayers?: (StaticMigrationWallAssemblyLayer | null)[];
}

/**
 * The output type from the migration.
 * This type needs to be converted to an EdgeBehavior
 */
interface WallAssemblyOperatorWithDisabledField {
  readonly wallAssemblyOperator: any;
  readonly name: string;
  readonly disabled?: any;
}

/**
 * This function parses DeprecatedWallAssemblyOverride and returns the left/right WallAssemblyOperators
 * for the override.
 */
function makeWallAssemblyOperatorsFromWallAssemblyOverride(
  wallAssemblyOverride: DeprecatedWallAssemblyOverride,
  staticRoom?: StaticMigrationRoom,
) {
  const {
    offset,
    structuralLayerOverrides = [],
    leftLayersOverrides = [],
    rightLayersOverrides = [],
    extraLeftFinishLayers = [],
    extraRightFinishLayers = [],
  } = wallAssemblyOverride;

  const leftOperators: WallAssemblyOperatorWithDisabledField[] = [];
  const rightOperators: WallAssemblyOperatorWithDisabledField[] = [];

  const makeInteriorCladdingLayerOperators = (
    layerOperators: WallAssemblyOperatorWithDisabledField[],
    layerOverrides: (Partial<StaticMigrationWallAssemblyLayer> | null)[],
    extraFinishLayers: (Partial<StaticMigrationWallAssemblyLayer> | null)[],
  ) => {
    if (layerOverrides.length) {
      // there should only be one layer override for interior cladding. anything else is an error
      // and can be ignored
      const finishLayerOverrides = layerOverrides.slice().filter((l) => !!l);
      const interiorFinishLayerOverride = finishLayerOverrides[0];
      if (interiorFinishLayerOverride) {
        layerOperators.push({
          name: "Interior Finish Layer",
          wallAssemblyOperator: wallAssemblyLayerOperatorOverrideNew({
            ...interiorFinishLayerOverride,
            type: "INTERIOR_FINISH",
          }),
        });
      }
    }

    for (const layer of extraFinishLayers) {
      const exteriorFinishLayerOverride = layer;
      layerOperators.push({
        name: "Interior Finish Layer",
        wallAssemblyOperator: wallAssemblyLayerOperatorAddNew({
          ...defaultWallAssemblyLayerDomain, // fill in missing info from the override
          ...exteriorFinishLayerOverride,
          type: "INTERIOR_FINISH",
        }),
      });
    }
  };

  const makeExteriorCladdingLayerOperators = (
    layerOperators: WallAssemblyOperatorWithDisabledField[],
    layerOverrides: Partial<StaticMigrationWallAssemblyLayer | null>[],
    extraFinishLayers: Partial<StaticMigrationWallAssemblyLayer | null>[],
  ) => {
    if (layerOverrides.length) {
      const substrateLayerOverride = layerOverrides[0];
      if (substrateLayerOverride) {
        const { disabled } = substrateLayerOverride;
        delete substrateLayerOverride.disabled;
        layerOperators.push({
          disabled,
          name: "Substrate Layer",
          wallAssemblyOperator: wallAssemblyLayerOperatorOverrideNew({
            ...substrateLayerOverride,
            type: "SUBSTRATE",
          }),
        });
      }
      const finishLayerOverrides = layerOverrides.slice(1).filter((l) => !!l);
      const exteriorFinishLayerOverride = finishLayerOverrides[0];
      if (exteriorFinishLayerOverride) {
        const { disabled } = exteriorFinishLayerOverride;
        delete exteriorFinishLayerOverride.disabled;
        layerOperators.push({
          disabled,
          name: "Exterior Finish Layer",
          wallAssemblyOperator: wallAssemblyLayerOperatorOverrideNew({
            ...exteriorFinishLayerOverride,
            type: "EXTERIOR_FINISH",
          }),
        });
      }
    }

    for (const layer of extraFinishLayers) {
      const exteriorFinishLayerOverride = layer;
      if (!exteriorFinishLayerOverride) {
        continue;
      }
      const { disabled } = exteriorFinishLayerOverride;
      delete exteriorFinishLayerOverride.disabled;
      layerOperators.push({
        disabled,
        name: "Exterior Finish Layer",
        wallAssemblyOperator: wallAssemblyLayerOperatorAddNew({
          ...defaultWallAssemblyLayerDomain, // fill in missing info from the override
          ...exteriorFinishLayerOverride,
          type: "EXTERIOR_FINISH",
        }),
      });
    }
  };

  if (structuralLayerOverrides.length) {
    const structuralLayerOverride = structuralLayerOverrides[0];
    if (structuralLayerOverride) {
      const { disabled } = structuralLayerOverride;
      delete structuralLayerOverride.disabled;
      if (typeof disabled === "object") {
        /**
         * Corrects a bug in the assignment of the disabled field to
         * WallAssemblyLayers by spread operating the override onto the layer
         * and therefore passing the resolved formula value onto the layer
         * rather than removing the override
         *
         * Here we need to manually disable the default structural layer
         * if the override is disabled to mimic this behavior
         */
        leftOperators.push({
          disabled: formulaInvert(disabled),
          name: "Default Structural Layer Removal (Migration)",
          wallAssemblyOperator: wallAssemblyLayerOperatorRemoveNew({
            type: "STRUCTURE",
            layerIndex: 0,
          }),
        });
      }
      if (!objectIsEmpty(structuralLayerOverride)) {
        /**
         * Corrects a bug in the creation of disabled default layers
         * where the override is just the disabled field with no content
         */
        leftOperators.push({
          disabled,
          name: "Structural Layer",
          wallAssemblyOperator: wallAssemblyLayerOperatorOverrideNew({
            ...structuralLayerOverride,
            type: "STRUCTURE",
          }),
        });
      }
    }

    const extraStructuralLayers = structuralLayerOverrides.slice(1);
    for (const layer of extraStructuralLayers) {
      const structuralLayerOverride = layer;
      if (!structuralLayerOverride) {
        continue;
      }

      const { disabled } = structuralLayerOverride;
      delete structuralLayerOverride.disabled;
      leftOperators.push({
        disabled,
        name: "Structural Layer",
        wallAssemblyOperator: wallAssemblyLayerOperatorAddNew({
          ...defaultWallAssemblyLayerDomain, // fill in missing info from the override
          ...structuralLayerOverride,
          type: "STRUCTURE",
        }),
      });
    }
  }

  if (offset) {
    if (typeof offset === "number") {
      leftOperators.push({
        name: "Wall Offset",
        wallAssemblyOperator: wallAssemblyOffsetOperatorNew(offset),
      });
    } else if (typeof offset === "object" && offset.type === "MATCH_EXPR") {
      const { defaultValue = 0, cases: offsetCases = [] } = offset; // we must have a default value here

      const newCases: typeof offsetCases = [];
      for (const offsetCase of offsetCases) {
        newCases.push({
          ...offsetCase,
          // we must have a default value here
          value: offsetCase?.value ?? 0,
        });
      }

      leftOperators.push({
        name: "Wall Offset",
        wallAssemblyOperator: wallAssemblyOffsetOperatorNew({
          ...offset,
          defaultValue,
          cases: newCases,
        }),
      });
      /**
       * I am leaving this here as a reference - this should be migrated at a later date to
       * operate like the below. the offset value is a conditional field, which means you can have a
       * conditional field on a conditionable EdgeBehavior. This is really not ideal and should be updated
       */
      // if (defaultValue) {
      //   leftOperators.push({
      //     name : "Wall Offset",
      //     wallAssemblyOperator: wallAssemblyOffsetOperatorNew(defaultValue),
      //   });
      // }
      // for (const { condition, value } of cases) {
      //   leftOperators.push({
      //     disabled: condition,
      //     name : "Wall Offset",
      //     wallAssemblyOperator: wallAssemblyOffsetOperatorNew(value),
      //   });
      // }
    }
  }

  makeExteriorCladdingLayerOperators(rightOperators, rightLayersOverrides, extraRightFinishLayers);
  if (staticRoom && staticRoomIsInterior(staticRoom)) {
    makeInteriorCladdingLayerOperators(leftOperators, leftLayersOverrides, extraLeftFinishLayers);
  } else {
    makeExteriorCladdingLayerOperators(leftOperators, leftLayersOverrides, extraLeftFinishLayers);
  }

  return { leftOperators, rightOperators };
}

function wallAssemblyOffsetOperatorNew(offset: number | OffsetMatchExpr) {
  return {
    operationType: "OFFSET",
    offset,
  };
}

function wallAssemblyLayerOperatorAddNew(layer: any) {
  return {
    operationType: "ADD",
    layer,
  };
}

function wallAssemblyLayerOperatorOverrideNew(layer: any, layerIndex = 0) {
  return {
    operationType: "OVERRIDE",
    layerIndex,
    layer,
  };
}

function wallAssemblyLayerOperatorRemoveNew(layer: any, layerIndex = 0) {
  return {
    operationType: "REMOVE",
    layerIndex,
    layer,
  };
}

const defaultWallAssemblyLayerDomain = {
  lowerDatum: {
    datumType: "VERTICAL",
    anchor: "BASE_WALL",
    justification: "BOTTOM_UP",
    offset: 0,
    offsetNormalized: true,
  } as any,
  upperDatum: {
    datumType: "VERTICAL",
    anchor: "BASE_WALL",
    justification: "TOP_DOWN",
    offset: 0,
    offsetNormalized: true,
  } as any,
  startDatum: {
    datumType: "HORIZONTAL",
    justification: "START_TO_END",
    offset: 0,
    offsetNormalized: true,
  } as any,
  endDatum: {
    datumType: "HORIZONTAL",
    justification: "END_TO_START",
    offset: 0,
    offsetNormalized: true,
  } as any,
};

function staticRoomIsInterior(staticRoom: StaticMigrationRoom): boolean {
  const roomTypeEnclosure = staticRoom.attributes?.roomEnclosure;
  if (roomTypeEnclosure) {
    return isInteriorRoomEnclosure[roomTypeEnclosure];
  }
  return isInteriorRoomEnclosureRoomType[staticRoom.type];
}

const isInteriorRoomEnclosure: { [roomEnclosure: string]: boolean } = {
  CONDITIONED: true,
  UNCONDITIONED: true,
};

const isInteriorRoomEnclosureRoomType = {
  MASTER_BED: true,
  MASTER_BATH: true,
  MASTER_CLOSET: true,
  MASTER_HALL: true,
  MASTER_PASS: true,
  MASTER_SEAT: true,
  MASTER_SUITE: true,
  MASTER_VESTIBULE: true,
  SHOWER: true,
  WATER_CLOSET: true,
  BATH_HALL: true,
  MASTER_VANITY: true,
  BEDROOM: true,
  BED_PASS: true,
  REACH_CLOSET: true,
  BED_CLOSET: true,
  DINING: true,
  GUEST_SUITE: true,
  WALK_IN_CLOSET: true,
  POWDER: true,
  FORMAL_DINING: true,
  FORMAL_LIVING: true,
  KITCHEN: true,
  KITCHEN_CABINETS: true,
  DINING_NOOK: true,
  REFRIGERATOR: true,
  KITCHEN_HALL: true,
  PANTRY: true,
  LIVING: true,
  LIBRARY: true,
  SCREEN: true,
  LIVING_HALL: true,
  OFFICE: true,
  BAY_WINDOW: true,
  COAT_CLOSET: true,
  BATHFULL: true,
  BATHFULL_1_DOOR: true,
  BATHHALF: true,
  JJ_BATH: true,
  BED_BATH: true,
  BATHTUB: true,
  ENTRY: true,
  FOYER: true,
  ENTRY_BUFFER: true,
  OPEN_BELOW: true,
  GENERAL: true,
  GALLERY: true,
  GENERAL_OPEN: true,
  CLOSET: true,
  SOLARIUM: true,
  READING_NOOK: true,
  FLEX: true,
  CABINETS: true,
  HALL: true,
  HALL_NO_WINDOW: true,
  HALL_NO_ENTRY: true,
  STAIRS: true,
  STAIRS_EDITOR: true,
  CORNER_HALL: true,
  HALL_CASED_OPENING: true,
  GARAGE: true,
  GARAGE_DETACH: true,
  UTIL_HALL: true,
  MECH: true,
  MUDROOM: true,
  DOG: true,
  JUMP: true,
  LAUNDRY: true,
  WORKSHOP: true,
  MUD_LAUNDRY: true,
  FIREPLACE: true,
  NONE: true,
  CHASE: true,
};

function objectIsEmpty(obj: object) {
  for (const id in obj) {
    if (obj.hasOwnProperty(id)) {
      return false;
    }
  }

  return true;
}

function formulaInvert(value: any): any {
  if (isFormula(value)) {
    if (value.formula && value.formula.trim().startsWith("not (")) {
      return formulaNew(value.formula.trim().slice(5, -1));
    } else {
      return formulaNew(`not (${value.formula.trim()})`);
    }
  } else {
    return value;
  }
}

function isFormula(v: any) {
  return !!v && typeof v === "object" && v.hasOwnProperty("type") && v.type === "FORMULA";
}

function formulaNew(formula: string) {
  return { type: "FORMULA", formula };
}
